import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { CMS_POST_API_CONFIG } from '../config';
import {
  PostType,
  PostTypePopulated,
  CreatePostTypeDto,
  UpdatePostTypeDto,
  PostTypeQueryParams,
  PostTypeResponse,
  PostTypeListResponse,
  PostTypeDeleteResponse,
  PublicPostTypeListResponse,
  ActivePostTypeListResponse
} from '../models/post-type.model';

/**
 * Service for managing post types via the CMS API
 * 
 * This service provides comprehensive methods for:
 * - CRUD operations on post types
 * - Organization-scoped post type management
 * - Type-safe API interactions with full error handling
 * 
 * @remarks
 * Post types are used to categorize blog posts within an organization.
 * Each post type must have a unique name within its organization.
 * Authentication is handled by HTTP interceptors provided by the consuming application.
 * 
 * All queries filter by isActive: true by default (soft delete support).
 * Post types are automatically populated with organization and creator details.
 * 
 * @example
 * ```typescript
 * // Inject the service
 * constructor(private postTypeService: PostTypeService) {}
 * 
 * // Create a post type
 * this.postTypeService.createPostType({
 *   typeName: 'Tutorial',
 *   organizationId: 'org123'
 * }).subscribe({
 *   next: (response) => console.log('Created:', response.data),
 *   error: (error) => console.error('Error:', error.message)
 * });
 * 
 * // Get all post types for an organization
 * this.postTypeService.getAllPostTypes({ organizationId: 'org123' })
 *   .subscribe(response => {
 *     console.log('Found', response.count, 'post types');
 *     console.log('Post types:', response.data);
 *   });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class PostTypeService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CMS_POST_API_CONFIG);
  private readonly apiUrl: string;

  // Emits whenever a post type is successfully created, regardless of caller,
  // so any consumer (e.g. a sidebar) can react without a shared parent/child link.
  private readonly _postTypeCreated = new Subject<PostTypePopulated>();
  readonly postTypeCreated$: Observable<PostTypePopulated> = this._postTypeCreated.asObservable();

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/post-types`;
    this.logDebug('PostTypeService initialized', { apiUrl: this.apiUrl });
  }

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Create a new post type
   * 
   * @param postTypeData - The post type data to create
   * @returns Observable of the created post type response
   * 
   * @throws {Error} 400 - Invalid post type data
   * @throws {Error} 401 - Unauthorized (no valid authentication)
   * @throws {Error} 403 - Access denied (user not a member of organization)
   * @throws {Error} 404 - Organization not found
   * @throws {Error} 409 - Post type with this name already exists in the organization
   * @throws {Error} 500 - Server error
   * 
   * @remarks
   * - Type name must be unique within the organization
   * - Type name must be 2-50 characters
   * - User must be a member of the organization
   * - Post type is created with isActive: true
   * - Returns populated response with organization and creator details
   * 
   * @example
   * ```typescript
   * const createDto: CreatePostTypeDto = {
   *   typeName: 'Tutorial',
   *   organizationId: '507f1f77bcf86cd799439012'
   * };
   * 
   * this.postTypeService.createPostType(createDto).subscribe({
   *   next: (response) => {
   *     console.log('Post type created:', response.data.typeName);
   *     console.log('Organization:', response.data.organizationId.name);
   *     console.log('Created by:', response.data.createdBy.name);
   *   },
   *   error: (error) => {
   *     if (error.message.includes('already exists')) {
   *       console.error('Duplicate post type name');
   *     }
   *   }
   * });
   * ```
   */
  createPostType(postTypeData: CreatePostTypeDto): Observable<PostTypeResponse> {
    this.logDebug('Creating post type', postTypeData);
    
    return this.http.post<PostTypeResponse>(this.apiUrl, postTypeData)
      .pipe(
        tap(response => {
          if (response?.success && response.data) {
            this._postTypeCreated.next(response.data);
          }
        }),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get all post types with optional organization filtering
   * 
   * @param params - Optional query parameters for filtering
   * @returns Observable of post type list response
   * 
   * @throws {Error} 401 - Unauthorized (no valid authentication)
   * @throws {Error} 403 - Access denied (user not a member of organization)
   * @throws {Error} 404 - Organization not found
   * @throws {Error} 500 - Server error
   * 
   * @remarks
   * Behavior depends on organizationId parameter:
   * - Without organizationId: Returns all post types from user's created organizations
   * - With organizationId: Returns post types for specified organization (if user has access)
   * 
   * Only returns active post types (isActive: true)
   * Results are sorted by creation date (newest first)
   * All responses include populated organization and creator details
   * 
   * @example
   * ```typescript
   * // Get all post types for a specific organization
   * this.postTypeService.getAllPostTypes({ organizationId: 'org123' })
   *   .subscribe(response => {
   *     console.log(`Found ${response.count} post types`);
   *     response.data.forEach(postType => {
   *       console.log(`- ${postType.typeName} (${postType.organizationId.name})`);
   *     });
   *   });
   * 
   * // Get all post types from user's organizations
   * this.postTypeService.getAllPostTypes().subscribe(response => {
   *   console.log('All accessible post types:', response.data);
   * });
   * ```
   */
  getAllPostTypes(params?: PostTypeQueryParams): Observable<PostTypeListResponse> {
    this.logDebug('Fetching all post types', params);
    
    const httpParams = this.buildHttpParams(params);
    
    return this.http.get<PostTypeListResponse>(this.apiUrl, { params: httpParams })
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get a specific post type by ID
   * 
   * @param postTypeId - The ID of the post type to retrieve
   * @returns Observable of the post type response
   * 
   * @throws {Error} 400 - Invalid post type ID format
   * @throws {Error} 401 - Unauthorized (no valid authentication)
   * @throws {Error} 403 - Access denied (user not a member of organization)
   * @throws {Error} 404 - Post type not found
   * @throws {Error} 500 - Server error
   * 
   * @remarks
   * - User must be a member of the organization that owns the post type
   * - Returns populated response with organization and creator details
   * - Only returns active post types (isActive: true)
   * 
   * @example
   * ```typescript
   * this.postTypeService.getPostTypeById('507f1f77bcf86cd799439011')
   *   .subscribe({
   *     next: (response) => {
   *       const postType = response.data;
   *       console.log('Post type:', postType.typeName);
   *       console.log('Organization:', postType.organizationId.name);
   *       console.log('Created by:', postType.createdBy.email);
   *       console.log('Created at:', postType.createdAt);
   *     },
   *     error: (error) => {
   *       if (error.message.includes('not found')) {
   *         console.error('Post type does not exist');
   *       }
   *     }
   *   });
   * ```
   */
  getPostTypeById(postTypeId: string): Observable<PostTypeResponse> {
    this.logDebug('Fetching post type by ID', { postTypeId });
    
    return this.http.get<PostTypeResponse>(`${this.apiUrl}/${postTypeId}`)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Update an existing post type
   * 
   * @param postTypeId - The ID of the post type to update
   * @param updateData - The data to update
   * @returns Observable of the updated post type response
   * 
   * @throws {Error} 400 - Invalid post type ID or update data
   * @throws {Error} 401 - Unauthorized (no valid authentication)
   * @throws {Error} 403 - Access denied (user not a member of organization)
   * @throws {Error} 404 - Post type not found
   * @throws {Error} 409 - Post type with this name already exists in the organization
   * @throws {Error} 500 - Server error
   * 
   * @remarks
   * - User must be a member of the organization that owns the post type
   * - Type name must remain unique within the organization
   * - Type name must be 2-50 characters
   * - Returns populated response with organization and creator details
   * - Updates the updatedAt timestamp automatically
   * 
   * @example
   * ```typescript
   * const updateDto: UpdatePostTypeDto = {
   *   typeName: 'Advanced Tutorial'
   * };
   * 
   * this.postTypeService.updatePostType('507f1f77bcf86cd799439011', updateDto)
   *   .subscribe({
   *     next: (response) => {
   *       console.log('Updated post type:', response.data.typeName);
   *       console.log('Last updated:', response.data.updatedAt);
   *     },
   *     error: (error) => {
   *       if (error.message.includes('already exists')) {
   *         console.error('Another post type with this name exists');
   *       }
   *     }
   *   });
   * ```
   */
  updatePostType(postTypeId: string, updateData: UpdatePostTypeDto): Observable<PostTypeResponse> {
    this.logDebug('Updating post type', { postTypeId, updateData });
    
    return this.http.put<PostTypeResponse>(`${this.apiUrl}/${postTypeId}`, updateData)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Delete a post type (soft delete)
   * 
   * @param postTypeId - The ID of the post type to delete
   * @returns Observable of the deletion response
   * 
   * @throws {Error} 400 - Invalid post type ID format
   * @throws {Error} 401 - Unauthorized (no valid authentication)
   * @throws {Error} 403 - Access denied (user not a member of organization)
   * @throws {Error} 404 - Post type not found
   * @throws {Error} 500 - Server error
   * 
   * @remarks
   * This is a SOFT DELETE operation:
   * - Post type is not physically removed from the database
   * - isActive flag is set to false
   * - Post type will no longer appear in queries
   * - Data is preserved for integrity purposes
   * - User must be a member of the organization that owns the post type
   * 
   * @example
   * ```typescript
   * this.postTypeService.deletePostType('507f1f77bcf86cd799439011')
   *   .subscribe({
   *     next: (response) => {
   *       console.log(response.message); // "Post type deleted successfully"
   *       this.refreshPostTypeList();
   *     },
   *     error: (error) => {
   *       console.error('Failed to delete:', error.message);
   *     }
   *   });
   * ```
   */
  deletePostType(postTypeId: string): Observable<PostTypeDeleteResponse> {
    this.logDebug('Deleting post type', { postTypeId });
    
    return this.http.delete<PostTypeDeleteResponse>(`${this.apiUrl}/${postTypeId}`)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Get all post types for a specific organization (convenience method)
   * 
   * @param organizationId - The ID of the organization
   * @returns Observable of post type list response
   * 
   * @remarks
   * This is a convenience wrapper around getAllPostTypes() with organizationId parameter.
   * Useful for cleaner code when you always have an organization ID.
   * 
   * @example
   * ```typescript
   * this.postTypeService.getPostTypesByOrganization('org123')
   *   .subscribe(response => {
   *     console.log('Post types for this org:', response.data);
   *   });
   * ```
   */
  getPostTypesByOrganization(organizationId: string): Observable<PostTypeListResponse> {
    return this.getAllPostTypes({ organizationId });
  }

  /**
   * Get all public post types for an organization (no authentication required)
   *
   * @param orgId - The ID of the organization
   * @returns Observable of post type list response
   *
   * @remarks
   * Calls GET /api/post-types/public?orgId={orgId}.
   * This endpoint is publicly accessible — no Bearer token needed.
   * Intended for use in public-facing reader apps (e.g. stratum-reader).
   *
   * @example
   * ```typescript
   * this.postTypeService.getPublicPostTypes('org123').subscribe(response => {
   *   console.log('Public post types:', response.data);
   * });
   * ```
   */
  getPublicPostTypes(orgId: string): Observable<PostTypeListResponse> {
    this.logDebug('Fetching public post types', { orgId });
    const httpParams = new HttpParams().set('orgId', orgId);
    return this.http
      .get<PostTypeListResponse>(`${this.apiUrl}/public`, { params: httpParams })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Get a lightweight public list of post types for an organisation (no auth required)
   *
   * @param orgId - The ID of the organisation
   * @returns Observable of {@link PublicPostTypeListResponse}
   *
   * @remarks
   * Calls `GET /api/post-types/public?orgId={orgId}`.
   * Only returns post types where `isActive === true` and `isPublic === true`.
   * No Bearer token is attached — safe to use in unauthenticated contexts.
   * Rate-limited to 50 requests / 15 min per IP on the server.
   * The `X-Cache-Status` response header (`HIT` | `MISS`) is logged in debug mode.
   *
   * @example
   * ```typescript
   * this.postTypeService.getPublicPostTypesByOrg('org123').subscribe(response => {
   *   console.log('Public post types:', response.data);
   * });
   * ```
   */
  getPublicPostTypesByOrg(orgId: string): Observable<PublicPostTypeListResponse> {
    this.logDebug('Fetching public post types (lightweight)', { orgId });
    const httpParams = new HttpParams().set('orgId', orgId);
    return this.http
      .get<PublicPostTypeListResponse>(`${this.apiUrl}/public`, {
        params: httpParams,
        observe: 'response'
      })
      .pipe(
        tap(response => {
          const cacheStatus = response.headers.get('X-Cache-Status');
          if (cacheStatus) {
            this.logDebug('X-Cache-Status', { cacheStatus });
          }
        }),
        map(response => response.body!),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get all active post type names for the current user's organization
   *
   * @returns Observable of {@link ActivePostTypeListResponse}
   *
   * @remarks
   * Calls `GET /api/post-types/active`.
   * Organization is derived automatically from the JWT `orgId` custom claim — no parameters needed.
   * Returns only `_id` and `typeName` for active post types, sorted alphabetically.
   * Intended for authenticated dropdowns, tag selectors, and form controls.
   * The `X-Cache-Status` response header (`HIT` | `MISS`) is logged in debug mode (TTL 5 min).
   *
   * @example
   * ```typescript
   * this.postTypeService.getActivePostTypes().subscribe(response => {
   *   console.log('Active post types:', response.data);
   * });
   * ```
   */
  getActivePostTypes(): Observable<ActivePostTypeListResponse> {
    this.logDebug('Fetching active post types for current org');
    return this.http
      .get<ActivePostTypeListResponse>(`${this.apiUrl}/active`, { observe: 'response' })
      .pipe(
        tap(response => {
          const cacheStatus = response.headers.get('X-Cache-Status');
          if (cacheStatus) {
            this.logDebug('X-Cache-Status', { cacheStatus });
          }
        }),
        map(response => response.body!),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Check if a post type name exists in an organization
   * 
   * @param typeName - The type name to check
   * @param organizationId - The organization ID to check within
   * @returns Observable<boolean> - True if the name exists, false otherwise
   * 
   * @remarks
   * This is a convenience method to check for duplicate names before creating/updating.
   * Case-sensitive comparison is used.
   * Only checks active post types (isActive: true).
   * 
   * @example
   * ```typescript
   * const typeName = 'Tutorial';
   * const orgId = 'org123';
   * 
   * this.postTypeService.postTypeExists(typeName, orgId).subscribe(exists => {
   *   if (exists) {
   *     console.warn('Post type name already in use');
   *   } else {
   *     // Proceed with creation
   *     this.createNewPostType(typeName, orgId);
   *   }
   * });
   * ```
   */
  postTypeExists(typeName: string, organizationId: string): Observable<boolean> {
    return this.getAllPostTypes({ organizationId }).pipe(
      map(response => {
        return response.data.some(pt => 
          pt.typeName.toLowerCase() === typeName.toLowerCase()
        );
      }),
      catchError(() => {
        // If error occurs, assume it doesn't exist
        return [false];
      })
    );
  }

  /**
   * Get only the post type names for an organization (convenience method)
   * 
   * @param organizationId - The organization ID
   * @returns Observable<string[]> - Array of post type names
   * 
   * @remarks
   * Useful for dropdowns or autocomplete fields.
   * Returns only the names, sorted alphabetically.
   * 
   * @example
   * ```typescript
   * this.postTypeService.getPostTypeNames('org123').subscribe(names => {
   *   console.log('Available post types:', names);
   *   // ['Article', 'Guide', 'Tutorial']
   *   this.postTypeOptions = names;
   * });
   * ```
   */
  getPostTypeNames(organizationId: string): Observable<string[]> {
    return this.getAllPostTypes({ organizationId }).pipe(
      map(response => 
        response.data
          .map(pt => pt.typeName)
          .sort((a, b) => a.localeCompare(b))
      )
    );
  }

  /**
   * Create multiple post types in batch
   * 
   * @param postTypes - Array of post type DTOs to create
   * @returns Observable<PostTypeResponse[]> - Array of created post type responses
   * 
   * @remarks
   * This is a convenience method that creates multiple post types sequentially.
   * If one fails, the observable will error and remaining types won't be created.
   * Consider using forkJoin for parallel creation with better error handling.
   * 
   * @example
   * ```typescript
   * const postTypes: CreatePostTypeDto[] = [
   *   { typeName: 'Tutorial', organizationId: 'org123' },
   *   { typeName: 'Guide', organizationId: 'org123' },
   *   { typeName: 'Article', organizationId: 'org123' }
   * ];
   * 
   * this.postTypeService.createPostTypes(postTypes).subscribe({
   *   next: (responses) => {
   *     console.log(`Created ${responses.length} post types`);
   *     responses.forEach(r => console.log('- ' + r.data.typeName));
   *   },
   *   error: (error) => console.error('Batch creation failed:', error)
   * });
   * ```
   */
  createPostTypes(postTypes: CreatePostTypeDto[]): Observable<PostTypeResponse[]> {
    return new Observable(observer => {
      const results: PostTypeResponse[] = [];
      let currentIndex = 0;

      const createNext = () => {
        if (currentIndex >= postTypes.length) {
          observer.next(results);
          observer.complete();
          return;
        }

        this.createPostType(postTypes[currentIndex]).subscribe({
          next: (response) => {
            results.push(response);
            currentIndex++;
            createNext();
          },
          error: (error) => {
            observer.error(error);
          }
        });
      };

      createNext();
    });
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Build HTTP query parameters from query params object
   * 
   * @param params - Query parameters object
   * @returns HttpParams instance
   * 
   * @private
   */
  private buildHttpParams(params?: PostTypeQueryParams): HttpParams {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key as keyof PostTypeQueryParams];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }

    return httpParams;
  }

  /**
   * Handle HTTP errors with detailed, user-friendly messages
   * 
   * @param error - The HTTP error response
   * @returns Observable that errors with a formatted error message
   * 
   * @private
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Network error: ${error.error.message}`;
      this.logDebug('Client-side error', error);
    } else {
      // Backend error
      this.logDebug('Server-side error', { status: error.status, error });

      switch (error.status) {
        case 400:
          errorMessage = error.error?.message || 'Invalid post type data. Please check your input.';
          break;
        case 401:
          errorMessage = 'Unauthorized. Please log in to continue.';
          break;
        case 403:
          errorMessage = error.error?.message || 'Access denied. You must be a member of the organization.';
          break;
        case 404:
          errorMessage = error.error?.message || 'Post type not found.';
          break;
        case 409:
          errorMessage = error.error?.message || 'Post type with this name already exists in the organization.';
          break;
        case 422:
          errorMessage = error.error?.message || 'Validation error. Please check your input.';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later.';
          break;
        case 503:
          errorMessage = 'Service temporarily unavailable. Please try again later.';
          break;
        default:
          errorMessage = error.error?.message || `Error ${error.status}: ${error.statusText}`;
      }
    }

    console.error('PostTypeService Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Log debug messages if debug mode is enabled in configuration
   * 
   * @param message - Debug message
   * @param data - Optional data to log
   * 
   * @private
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[PostTypeService] ${message}`, data || '');
    }
  }
}
